<template>
    <div class="phoneNumberInput">
        <div class="phoneNumberLabelRow">
            <div class="phoneNumberLabelColumn">
                <div class="label-bold">
                    <label :for="inputId">{{ phoneNumberLabel }}</label>
                </div>
            </div>
        </div>
        <div class="phoneNumberRow">
            <div class="phoneCountryCodeColumn">
                <div class="phoneCountryCode">
                    <vue-multiselect :id="countrySelectId" placeholder="" v-model="selectedCountryCode" :options="supportedCountryCodes" @remove="clearButtonSelected"
                                     track-by="Code" label="Code" :option-height="30" :max-height="300" :show-labels="false" @select="phoneCountryCodeChanged">
                        <template #singleLabel="props">
                            <img v-if="selectedCountryCode?.Code != ''" class="option__image" :src="getFlag(props?.option?.Code)" /> &nbsp;
                            <span class="countryCode">{{ props?.option?.Code }}</span>
                            <template v-if="props.option.Ext != ''">
                                <span class="ext">({{ props?.option?.Ext }})</span>
                            </template>
                        </template>
                        <template #option="props">
                            <img class="option__image" :src="getFlag(props?.option?.Code)" />&nbsp;
                            <span class="countryCode">{{ props?.option?.Code }}</span>
                            <template v-if="props.option.Ext != ''">
                                <span class="ext">({{ props?.option?.Ext }})</span>
                            </template>
                        </template>
                        <template #noResult>
                            {{ noMatchingCountriesLabel }}
                        </template>
                    </vue-multiselect>
                </div>
            </div>
            <div class="phoneNumberColumn">
                <input :id="inputId"
                       type="tel"
                       inputmode="tel"
                       autocomplete="tel-national"
                       v-bind:value="phoneNumber"
                       v-bind:placeholder="placeholder"
                       v-on:input="phoneChanged($event)" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
  import { CountryCode } from '@/types/phoneCountryCodes';
  import parsePhoneNumber, { CountryCode as CountryCodeEnum, getExampleNumber, isSupportedCountry } from 'libphonenumber-js';
  import GetCountryFlagUrl from '../services/CountryFlag';
  import examples from 'libphonenumber-js/mobile/examples';
  import * as Vue from 'vue';

  // A controlled phone input split into two values, each with its own prop and event (there is no v-model): the
  // parent passes `phoneCountryCode` (an entry of `phoneCountryCodes`, matched by Code) and `phoneNumber` (the NATIONAL
  // number, no dial code), and must write back what `updatePhoneCountryCode` / `updatePhoneNumber` emit. Once a
  // country is selected the number is reformatted for it as the user types, keeping the caret beside the digit just
  // typed or deleted. `updatePhoneNumberError` only ever emits '' (clearing a parent's error when the country is
  // cleared on an empty number).
  export default Vue.defineComponent({
    name: 'PhoneNumberInput',
    props: {
      phoneNumberLabel: {
        type: String,
        default: "Phone Number"
      },
      noMatchingCountriesLabel: {
        type: String,
        default: "Sorry, no matching countries."
      },
      // Every country the parent offers. Never mutated: countries the phone library cannot format are filtered into
      // `supportedCountryCodes` instead.
      phoneCountryCodes: {
        type: Array as Vue.PropType<CountryCode[]>,
        required: true
      },
      // Typed as an object, not the CountryCode class: the PhoneCountryCodes entries are plain { Code, Ext } objects,
      // which a class prop type would reject at runtime.
      phoneCountryCode: {
        type: Object as Vue.PropType<CountryCode>,
        required: false,
        default: undefined
      },
      phoneNumber: {
        type: String,
        default: ""
      },
      // Element ids. The defaults are the ids existing pages style against; set unique values when more than one
      // instance can be on a page.
      inputId: {
        type: String,
        default: "phoneNumber"
      },
      countrySelectId: {
        type: String,
        default: "countryCodeSelect"
      }
    },
    emits: {
      updatePhoneNumber: (_phone: string) => true,
      updatePhoneCountryCode: (_countryCode: CountryCode) => true,
      updatePhoneNumberError: (_error: string) => true
    },
    data() {
      return {
        // Mirrors the phoneNumber prop. Seeded from it so a component mounted with a number reformats that number when
        // the country changes; the watcher below keeps it in step with later prop changes.
        phone: this.phoneNumber,
        placeholder: '',
        selectedCountryCode: undefined as CountryCode | undefined
      }
    },
    computed: {
      supportedCountryCodes(): CountryCode[] {
        return this.phoneCountryCodes.filter(code => code != null && isSupportedCountry(code.Code));
      }
    },
    watch: {
      // Applies the parent's country — on creation and whenever it changes — and brings the placeholder and the
      // number's formatting in line with it. A parent that loads its country asynchronously is covered by the same
      // watcher, so no polling is needed.
      phoneCountryCode: {
        handler(newVal: CountryCode | undefined, oldVal: CountryCode | undefined) {
          if (newVal == undefined && oldVal == undefined) {
            this.selectedCountryCode = undefined;
            return;
          }

          this.selectedCountryCode = newVal ?? new CountryCode();
          this.setPhonePlaceholder(this.selectedCountryCode);

          if (this.phone.length > 0)
            this.phone = this.formatPhoneNumber(this.selectedCountryCode, this.phone);
        },
        immediate: true
      },
      phoneNumber: function (newVal: string) {
        this.phone = newVal;
      }
    },
    methods: {
      getFlag(countryCode: string) {
        return GetCountryFlagUrl(countryCode);
      },
      clearButtonSelected() {
        this.selectedCountryCode = undefined;

        if (this.phoneNumber == '') {
          this.placeholder = '';
          this.$emit('updatePhoneNumberError', '');
        }

        this.$emit('updatePhoneCountryCode', new CountryCode());
      },
      phoneCountryCodeChanged(countryCode: CountryCode) {
        if (this.selectedCountryCode != undefined && this.phone.length > 0) {
          let formattedPhoneNumber = this.formatPhoneNumber(this.selectedCountryCode, this.phone);

          if (formattedPhoneNumber != this.phone) {
            this.phone = formattedPhoneNumber;
            this.$emit('updatePhoneNumber', formattedPhoneNumber);
          }
        }

        this.setPhonePlaceholder(countryCode);
        this.$emit('updatePhoneCountryCode', countryCode);
      },
      phoneChanged(event: Event) {
        const input = event.target as HTMLInputElement;
        let phone = input.value;

        if (this.phone.length > 0 && phone.length <= 0 && this.selectedCountryCode == undefined) {
          this.setPhonePlaceholder(new CountryCode());
        }

        // The caret's position measured in digits, which formatting never changes (it only adds or drops punctuation).
        let digitsBeforeCaret = this.countDigits(phone.slice(0, input.selectionStart ?? phone.length));

        if (this.selectedCountryCode != undefined) {
          // Backspacing over formatting punctuation (e.g. the ")" in "(503) 7") removes no digit, and reformatting
          // would simply put the punctuation back. Treat it as deleting the digit before the caret instead.
          const deletedOnlyPunctuation = (event as InputEvent).inputType === 'deleteContentBackward'
            && this.countDigits(phone) === this.countDigits(this.phone);
          if (deletedOnlyPunctuation && digitsBeforeCaret > 0) {
            const digits = phone.replace(/\D/g, '');
            phone = digits.slice(0, digitsBeforeCaret - 1) + digits.slice(digitsBeforeCaret);
            digitsBeforeCaret--;
          }

          phone = this.formatPhoneNumber(this.selectedCountryCode, phone);
        }

        this.phone = phone;
        this.$emit('updatePhoneNumber', phone);

        // After the parent writes the value back and the input re-renders, make sure it shows the formatted value
        // (a parent value that did not change does not re-render it) and put the caret back after the same digit.
        this.$nextTick(() => {
          if (input.value !== phone)
            input.value = phone;
          if (document.activeElement === input) {
            const caret = this.caretAfterDigits(phone, digitsBeforeCaret);
            input.setSelectionRange(caret, caret);
          }
        });
      },
      countDigits(value: string): number {
        return value.replace(/\D/g, '').length;
      },
      // The string index just after the n-th digit of `value` (0 when n is 0, the end when there are fewer digits).
      caretAfterDigits(value: string, digits: number): number {
        if (digits <= 0)
          return 0;
        let seen = 0;
        for (let index = 0; index < value.length; index++) {
          if (/\d/.test(value[index]) && ++seen === digits)
            return index + 1;
        }
        return value.length;
      },
      setPhonePlaceholder(countryCode: CountryCode) {
        if (JSON.stringify(countryCode) == JSON.stringify(new CountryCode)) {
          this.placeholder = '';
          return;
        }

        var countryCodeEnum = countryCode.Code as CountryCodeEnum;
        const examplePhoneNumber = getExampleNumber(countryCodeEnum, examples);

        if (examplePhoneNumber) {
          var formatNational = examplePhoneNumber.formatNational();

          if (formatNational && formatNational.length > 0) {
            var placeholder = '';
            
            for (var placeHolderIndex = 0; placeHolderIndex < formatNational.length; placeHolderIndex++) {
              var value = formatNational[placeHolderIndex];

              if (value == ' ' || value == '-') {
                placeholder += ' ';
              }
              else if (value == '(' || value == ')') {
                placeholder += '';
              }
              else {
                placeholder += "x";
              }
            }

            this.placeholder = placeholder;
          }
        }
      },
      formatPhoneNumber(countryCode: CountryCode, phoneNumber: string) {
        if (JSON.stringify(countryCode) == JSON.stringify(new CountryCode))
          return phoneNumber;

        var countryCodeEnum = countryCode.Code as CountryCodeEnum;
        let fullNumber = '+' + countryCode.Ext + phoneNumber;
        let parsedPhoneNumber = parsePhoneNumber(fullNumber, countryCodeEnum);

        return parsedPhoneNumber
          ? parsedPhoneNumber.formatNational()
          : phoneNumber;
      }
    },
  })
</script>

<style lang="scss">
  .multiselect, multiselect--active {
    max-height: $standard_space * 2;
  }

  .multiselect__option--highlight {
    background: $primary_button_color;
  }

  .multiselect__content {
    min-width: 100%;
    width: 6rem;
  }

  .multiselect__single {
    white-space: nowrap;
  }

  .multiselect__content-wrapper{
    min-width: 100%;
    width: 8rem;
  }

  .option__image {
    max-width: 24px;
  }

  .phoneNumberLabelRow {
    display: flex;
    max-width: 350px;
  }

  .phoneNumberLabelRow > .phoneNumberLabelColumn {
    flex: 1 1 1;
    min-width: 150px;
    max-width: 175px;
    margin-right: 4px;
  }

  .phoneNumberLabelRow > .phoneNumberLabelColumn {
    flex: 1 1 2;
  }

  .phoneNumberRow {
    max-height: $standard_space * 2;
    display: flex;
    max-width: 350px;
  }

  .phoneNumberRow > .phoneCountryCodeColumn {
    flex: 1 1 1;
    min-width: 9rem;
    max-width: 9rem;
    margin-right: 4px;

    .countryCode {
      color: $primary_text;
    }

    .ext {
      font-size: 0.8rem;
      color: $faint_text;
      padding-left: 2px;
    }
  }

  .phoneNumberRow > .phoneNumberColumn {
    width: 100%;

    @media screen and ( max-width: 500px ) {
      
      input {  
        max-width: 150px;
      }
    }
  }
</style>
